/**
 * Profile Hooks
 * Fetch and update combined profile data (user + user_settings)
 */

import { type QueryClient, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { type PrivateProfile, type UpdateProfileRequest } from '@/lib/api/v2/profile';
import { profileApi } from '@/lib/api/v2/profile';
import { uploadProfilePictureServer } from '@/lib/server-fns/profile-picture-fns';
import { logger } from '@/lib/utils/logger';

type SettingsCache = {
  profile?: {
    display_name?: string | null;
    actual_name?: string | null;
    bio?: string | null;
    profile_picture_url?: string | null;
  };
  [key: string]: unknown;
};

type SessionCache = {
  display_name?: string | null;
  profile_picture_url?: string | null;
  bio?: string | null;
  [key: string]: unknown;
};

export function useProfile(initialData?: PrivateProfile | null) {
  return useQuery({
    queryKey: ['profile'],
    // ✅ Client-side API call to avoid import protection issues
    queryFn: async () => {
      logger.info('useProfile', 'Fetching profile via v2 API');
      const result = await profileApi.getProfile();
      if (!result.ok) {
        throw new Error(result.error.message);
      }
      return result.value;
    },
    initialData: initialData ?? undefined,
    staleTime: 1000 * 60 * 5,
    gcTime: 1000 * 60 * 30,
    retry: 1,
  });
}

export function useUpdateProfile() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (updates: UpdateProfileRequest) => {
      logger.info('useUpdateProfile', 'Updating profile via server function', { updates });
      // Use server function
      const { updateProfileServer } = await import('@/lib/server-fns/profile-fns');
      return await updateProfileServer({ data: updates });
    },
    onSuccess: (data: PrivateProfile) => {
      // Update profile cache
      queryClient.setQueryData<PrivateProfile>(['profile'], data);

      // Keep settings cache in sync for components still reading it
      queryClient.setQueryData<SettingsCache | undefined>(['settings'], (prev) => {
        if (!prev) return prev;
        return {
          ...prev,
          profile: {
            ...(prev.profile ?? {}),
            display_name: data.display_name ?? prev.profile?.display_name,
            actual_name: data.actual_name ?? prev.profile?.actual_name,
            bio: data.bio ?? prev.profile?.bio,
            profile_picture_url: data.profile_picture_url ?? prev.profile?.profile_picture_url,
          },
        };
      });

      // Update auth session cache so greetings reflect new display name
      queryClient.setQueryData<SessionCache | undefined>(['auth', 'session'], (prev) => {
        if (!prev) return prev;
        return {
          ...prev,
          display_name: data.display_name ?? prev.display_name,
          profile_picture_url: data.profile_picture_url ?? prev.profile_picture_url,
          bio: data.bio ?? prev.bio,
        };
      });

      logger.info('useUpdateProfile', 'Profile updated successfully');
    },
  });
}

// Refetch without blocking the mutation: mutateAsync awaits onSuccess, so
// awaited invalidations kept the upload spinner running through both refetches
// (the forced session refetch alone can take seconds; see useSession).
const invalidateInBackground = (queryClient: QueryClient, queryKey: readonly string[]) => {
  queryClient.invalidateQueries({ queryKey }).catch((error) => {
    logger.warn('useProfile', 'Background refetch failed', { queryKey, error });
  });
};

export function useUploadProfilePicture() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (file: File) => {
      const formData = new FormData();
      formData.append('file', file);
      return await uploadProfilePictureServer({ data: formData });
    },
    onSuccess: () => {
      invalidateInBackground(queryClient, ['profile']);
      invalidateInBackground(queryClient, ['auth', 'session']);
    },
  });
}
